import { useCallback, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDispatch } from 'react-redux';
import {
  toggleApprovalAssetModal,
  toggleApprovalModal
} from '@redux/actions/modalAction';
import { useCurrentWorkspace } from '@hooks/workspace';
import useOnboardingProcess from '@hooks/useOnboardingProcess';
import type { AwState } from '@components/Modals/ApprovalModal/ApprovalModal/ApprovalModal';
import type { StepItemKey } from '@components/Modals/ApprovalModal/ApprovalModal/components/Steps/Steps';
import store from '@redux/store';
import { ApprovalWorkflowItemDto } from '@api/Api';
import { useIsDemoOrganization } from '@hooks/useIsDemoOrganization';

export default function useApprovalModalTour({
  campaignId,
  awState,
  approvalWorkflow,
  setAwState,
  setCurrentStep
}: {
  campaignId: string;
  awState: AwState | null;
  approvalWorkflow?: ApprovalWorkflowItemDto;
  setAwState: (value: AwState | null) => void;
  setCurrentStep: (value: StepItemKey) => void;
}) {
  const navigate = useNavigate();
  const { onStartTour, tourData } = useOnboardingProcess();
  const dispatch = useDispatch();
  const [currentWorkspace] = useCurrentWorkspace(false);
  const isDemoOrganization = useIsDemoOrganization();

  const setAwStateRef = useRef(setAwState);
  setAwStateRef.current = setAwState;
  const setCurrentStepRef = useRef(setCurrentStep);
  setCurrentStepRef.current = setCurrentStep;
  const prevAwState = useRef(awState);
  const approvalWorkflowRef = useRef<ApprovalWorkflowItemDto | null>(null);
  if (!prevAwState.current) prevAwState.current = awState;
  if (approvalWorkflow) approvalWorkflowRef.current = approvalWorkflow;

  const onToggleOpenModal = useCallback(
    (isOpen = false) => {
      const state = store.getState().modal.approvalModal;
      dispatch(
        toggleApprovalModal(
          state
            ? { ...state, openStep: 'creatingApproval', visible: isOpen }
            : null
        )
      );
    },
    [dispatch]
  );

  useEffect(() => {
    if (!isDemoOrganization) return;
    if (tourData?.type !== 'route-for-approval') return;
    onStartTour({
      type: 'route-for-approval',
      onClickNext: async (currentStep, isExitTour) => {
        const _approvalWorkflow = approvalWorkflowRef.current;
        const _awState = prevAwState.current;
        if (isExitTour) return;
        if (!_approvalWorkflow) return;
        if (!_awState) return;
        if (currentStep === 1) {
          onToggleOpenModal(true);
          return;
        }
        if (currentStep === 2) {
          setAwStateRef.current({
            ..._awState,
            name: _awState.name,
            stages: _awState.stages.slice(0, 1)
          });
          return;
        }
        if (currentStep === 3) {
          setCurrentStepRef.current('media');
          return;
        }
        if (currentStep === 4) {
          setCurrentStepRef.current('stages');
        }
        if (currentStep === 6 && prevAwState.current) {
          setAwStateRef.current({
            ...prevAwState.current,
            stages: _awState.stages
          });
          return;
        }
        if (currentStep === 7) {
          setCurrentStepRef.current('settings');
          return;
        }
        if (currentStep === 8) {
          setCurrentStepRef.current('finalReview');
          return;
        }
        if (currentStep === 10) {
          await navigate(
            `/campaigns/approvals/${_approvalWorkflow.workspaceId}/${_approvalWorkflow.campaignId}/${_approvalWorkflow.id}`
          );
          onToggleOpenModal(false);
        }
      },
      onClickPrev: (currentStep, isExitTour) => {
        if (isExitTour) return;
        if (currentStep === 1) {
          // go back and use useApprovalСampaignsTour hook
          navigate(
            `/campaigns/all/${currentWorkspace?.id}?startTour=route-for-approval`
          );
          return;
        }
        if (currentStep === 2) {
          navigate(`/campaigns/single/${campaignId}`);
          onToggleOpenModal();
          return;
        }
        if (currentStep === 4) {
          setCurrentStepRef.current('creatingApproval');
          return;
        }
        if (currentStep === 5) {
          setCurrentStepRef.current('media');
          if (prevAwState.current) {
            setAwStateRef.current({
              ...prevAwState.current,
              stages: prevAwState.current.stages.slice(0, 1)
            });
          }
          return;
        }
        if (currentStep === 7 && prevAwState.current) {
          setAwStateRef.current({
            ...prevAwState.current,
            stages: prevAwState.current.stages.slice(0, 1)
          });
        }
        if (currentStep === 8) {
          setCurrentStepRef.current('stages');
          return;
        }
        if (currentStep === 9) {
          setCurrentStepRef.current('settings');
          return;
        }
        if (currentStep === 11 && approvalWorkflowRef.current) {
          dispatch(
            toggleApprovalModal({
              visible: true,
              approvalWorkflowId: approvalWorkflowRef.current.id,
              defaultAwMode: 'create',
              openStep: 'finalReview',
              campaignId: approvalWorkflowRef.current.campaignId,
              permissions: {
                create: true
              }
            })
          );
        }
      },
      onClose: (currentStep) => {
        onToggleOpenModal();
        // click on last step
        if (currentStep === 12 && !!approvalWorkflowRef.current?.assets?.[0]) {
          dispatch(
            toggleApprovalAssetModal({
              visible: true,
              assetId: approvalWorkflowRef.current.assets[0].id,
              versionId: approvalWorkflowRef.current.assets[0].versionId,
              approvalWorkflowId: approvalWorkflowRef.current.id,
              callback: (aw) => {
                if (!aw) {
                  navigate(
                    `/campaigns/single/${approvalWorkflowRef.current?.campaignId}?tab=approvals`
                  );
                }
              }
            })
          );
          return;
        }
        dispatch(toggleApprovalAssetModal(null));
      }
    });
  }, [
    isDemoOrganization,
    campaignId,
    tourData?.type,
    currentWorkspace?.id,
    dispatch,
    navigate,
    onToggleOpenModal,
    onStartTour
  ]);

  return null;
}
